import React, { useState } from 'react';
import { Button, IconSpinner, Modal, IconCollection } from '../../../components';
import { FormattedMessage } from 'react-intl';
import css from './VerificationModel.module.css';
import { createResourceLocatorString } from '../../../util/routes';

const VerificationModel = (rootProps) => {
    const {
        isOpen,
        onClose,
        onManageDisableScrolling,
        usePortal,
        containerClassName,
        contentClassName,
        isVerificationInProgress,
        onUpdateUserProfile,
        setIsVerificationInProgress,
        currentUser,
        onRefreshTransactionEntityAgain,
        transactionId,
        isVerificationFailed,
        setIsVerificationFailed,
        history,
        routeConfiguration,
        personaVerificationAtTransactionPage = null
    } = rootProps;


    const [isVerificationCompleted, setIsVerificationCompleted] = useState(false);
    const handleStart = async () => {
        if (typeof window === "undefined") return;
        try {
            setIsVerificationInProgress(true);
            const userId = currentUser?.id?.uuid;
            const module = await import("persona");
            const Persona = module.default || module;
            const referenceId = transactionId?.uuid ? `${userId}:${transactionId?.uuid}` : userId;

            const client = new Persona.Client({
                templateId: process.env.REACT_APP_PERSONA_SANDBOX_TEMPLATE_ID,
                environment: (process.env.REACT_APP_PROJECT_ENV == "production") ? "production" : "sandbox",
                referenceId: referenceId,
                onReady: () => client.open(),
                onComplete: () => {
                    if (personaVerificationAtTransactionPage) {
                        onUpdateUserProfile({ publicData: { personaVerificationAtTransactionPage } })
                            .catch(() => {
                                // Error is already handled via Redux state
                                // This catch prevents unhandled promise rejection
                            });
                    }
                    setTimeout(() => {
                        if (transactionId && onRefreshTransactionEntityAgain) {
                            onRefreshTransactionEntityAgain(transactionId);
                        }
                        setIsVerificationInProgress(false);
                        setIsVerificationCompleted(true);
                        // We just need to reload the page to get the latest data from the server
                        window.location.reload();
                    }, 1000);
                },
                onCancel: () => {
                    setIsVerificationFailed(true);
                    setTimeout(() => {
                        if (transactionId && onRefreshTransactionEntityAgain) {
                            onRefreshTransactionEntityAgain(transactionId);
                        }
                        setIsVerificationInProgress(false);
                    }, 1000);
                },
                onError: (error) => {
                    setIsVerificationFailed(true);
                    setTimeout(() => {
                        setIsVerificationInProgress(false);
                        if (transactionId && onRefreshTransactionEntityAgain) {
                            onRefreshTransactionEntityAgain(transactionId);
                        }
                    }, 1000);
                    console.error("Failed to load Persona in the browser:", error);
                },
            });
        } catch (error) {
            console.error("Failed to load Persona in the browser:", error);
        }
    };

    return (
        <div>
            <Modal
                id="VerificationModel"
                isOpen={isOpen}
                onClose={onClose}
                onManageDisableScrolling={onManageDisableScrolling}
                usePortal={usePortal}
                containerClassName={containerClassName}
                contentClassName={contentClassName}
                className={css.modalRoot}
            >
                {isVerificationCompleted ? <div className={css.root}>
                    {/* Header Section */}
                    <div className={css.headerSection} style={{ border: "unset", paddingBottom: 0 }}>
                        <div className={css.shieldIcon}>
                            <IconCollection name="verifiedShieldSuccess" />
                        </div>
                        <h1 className={css.title}>
                            <FormattedMessage
                                id="VerificationModel.completedTitle"
                                defaultMessage="Verification Complete!"
                            />
                        </h1>
                        <p className={css.description}>
                            <FormattedMessage
                                id="VerificationModel.completedDescription1"
                                defaultMessage="Your identity has been successfully verified."
                            />
                        </p>
                        <p className={css.description}>
                            <FormattedMessage
                                id="VerificationModel.completedDescription2"
                                defaultMessage="We've notified the owner so they can proceed with your booking."
                            />
                        </p>
                    </div>
                    {/* CTA Section */}
                    <div className={css.ctaSection}>
                        {isVerificationInProgress ? (
                            <div className={css.spinner}>
                                <IconSpinner />
                            </div>
                        ) : (
                            <>
                                <Button

                                    className={css.verifyButton}
                                    onClick={() => {
                                        return history.push(createResourceLocatorString("InboxPage", routeConfiguration, {}, { tab: 'orders' }))
                                        // Here redirect to persona and read and verify the current User
                                    }}
                                >
                                    <FormattedMessage
                                        id="VerificationModel.continueToInboxButton"
                                        defaultMessage="Continue to Bookings"
                                    />
                                </Button>


                            </>
                        )}
                    </div>
                </div>
                    : isVerificationFailed ?
                        <div className={css.root}>
                            {/* Header Section */}
                            <div className={css.headerSection} style={{ border: "unset", paddingBottom: 0 }}>
                                <div className={css.shieldIcon}>
                                    <IconCollection name="verificationShieldError" />
                                </div>
                                <h1 className={css.title}>
                                    <FormattedMessage
                                        id="VerificationModel.errorTitle"
                                        defaultMessage="Verification Unsuccessful"
                                    />
                                </h1>
                                <p className={css.description}>
                                    <FormattedMessage
                                        id="VerificationModel.errorDescription"
                                        defaultMessage="We weren’t able to verify your ID this time — it could be due to unclear images or mismatched details."
                                    />
                                </p>

                            </div>
                            {/* CTA Section */}
                            <div className={css.ctaSection}>
                                {isVerificationInProgress ? (
                                    <div className={css.spinner}>
                                        <IconSpinner />
                                    </div>
                                ) : (
                                    <>
                                        <Button

                                            className={css.verifyButton}
                                            onClick={() => {
                                                handleStart();
                                                // Here redirect to persona and read and verify the current User
                                            }}
                                        >
                                            <FormattedMessage
                                                id="VerificationModel.reTryVerifyButton"
                                                defaultMessage="Retry Verification"
                                            />
                                        </Button>


                                    </>
                                )}
                            </div>
                        </div>
                        :
                        <div className={css.root}>
                            {/* Header Section */}
                            <div className={css.headerSection}>
                                <div className={css.shieldIcon}>
                                    <IconCollection name="verificationShieldStar" />
                                </div>
                                <h1 className={css.title}>
                                    <FormattedMessage
                                        id="VerificationModel.title"
                                        defaultMessage="Verify your identity to continue"
                                    />
                                </h1>
                                <p className={css.description}>
                                    <FormattedMessage
                                        id="VerificationModel.description"
                                        defaultMessage="To keep our community safe and trustworthy, we verify the identity of renters for select bookings."
                                    />
                                </p>
                            </div>

                            {/* Features Section */}
                            <div className={css.featuresSection}>
                                {/* Trusted & Secure */}
                                <div className={css.featureItem}>
                                    <div className={css.featureIcon}>
                                        <IconCollection name="shieldCheckmark" />
                                    </div>
                                    <div className={css.featureContent}>
                                        <h3 className={css.featureTitle}>
                                            <FormattedMessage
                                                id="VerificationModel.trustedSecureTitle"
                                                defaultMessage="Trusted & Secure"
                                            />
                                        </h3>
                                        <p className={css.featureDescription}>
                                            <FormattedMessage
                                                id="VerificationModel.trustedSecureDescription"
                                                defaultMessage="Verification is powered by Persona, using advanced encryption and strict privacy safeguards."
                                            />
                                        </p>
                                    </div>
                                </div>

                                {/* Quick & Easy */}
                                <div className={css.featureItem}>
                                    <div className={css.featureIcon}>
                                        <IconCollection name="idCardClock" />
                                    </div>
                                    <div className={css.featureContent}>
                                        <h3 className={css.featureTitle}>
                                            <FormattedMessage
                                                id="VerificationModel.quickEasyTitle"
                                                defaultMessage="Quick & Easy"
                                            />
                                        </h3>
                                        <p className={css.featureDescription}>
                                            <FormattedMessage
                                                id="VerificationModel.quickEasyDescription"
                                                values={{
                                                    text: <span><strong>Driver’s License </strong>, <strong>Passport</strong>, or <strong>National ID Card</strong></span>
                                                }}
                                                defaultMessage="You'll need a valid government-issued ID — the process usually takes less than 2 minutes."
                                            />
                                        </p>
                                    </div>
                                </div>

                                {/* Private & Confidential */}
                                <div className={css.featureItem}>
                                    <div className={css.featureIcon}>
                                        <IconCollection name="padlockSecure" />
                                    </div>
                                    <div className={css.featureContent}>
                                        <h3 className={css.featureTitle}>
                                            <FormattedMessage
                                                id="VerificationModel.privateConfidentialTitle"
                                                defaultMessage="Private & Confidential"
                                            />
                                        </h3>
                                        <p className={css.featureDescription}>
                                            <FormattedMessage
                                                id="VerificationModel.privateConfidentialDescription"
                                                defaultMessage="Your personal details are never shared with owners or other users. They're used only to confirm your identity."
                                            />
                                        </p>
                                    </div>
                                </div>

                                {/* Required for High-Value Rentals */}
                                <div className={css.featureItem}>
                                    <div className={css.featureIcon}>
                                        <IconCollection name="cubeBox" />
                                    </div>
                                    <div className={css.featureContent}>
                                        <h3 className={css.featureTitle}>
                                            <FormattedMessage
                                                id="VerificationModel.highValueRentalsTitle"
                                                defaultMessage="Required for High-Value Rentals"
                                            />
                                        </h3>
                                        <p className={css.featureDescription}>
                                            <FormattedMessage
                                                id="VerificationModel.highValueRentalsDescription"
                                                defaultMessage="This helps protect both renters and owners when booking expensive or short-notice items."
                                            />
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* CTA Section */}
                            <div className={css.ctaSection}>
                                {isVerificationInProgress ? (
                                    <div className={css.spinner}>
                                        <IconSpinner />
                                    </div>
                                ) : (
                                    <>
                                        <Button

                                            className={css.verifyButton}
                                            onClick={() => {
                                                handleStart();
                                                // Here redirect to persona and read and verify the current User
                                            }}
                                        >
                                            <FormattedMessage
                                                id="VerificationModel.verifyButton"
                                                defaultMessage="Verify with Persona"
                                            />
                                        </Button>

                                        <p className={css.readMoreLink}>
                                            <a
                                                href="https://withpersona.com/privacy"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <FormattedMessage
                                                    id="VerificationModel.readMore"
                                                    defaultMessage="Read more"
                                                />
                                            </a>{' '}
                                            <FormattedMessage
                                                id="VerificationModel.readMoreText"
                                                defaultMessage="about Persona's privacy and verification process."
                                            />
                                        </p>

                                        <p className={css.privacyDisclaimer}>
                                            <FormattedMessage
                                                id="VerificationModel.privacyDisclaimer"
                                                defaultMessage="Persona Privacy Terms apply. Your information is securely processed by Persona and stored with industry-leading encryption."
                                            />
                                        </p>
                                    </>
                                )}
                            </div>
                        </div>}
            </Modal>
        </div>
    );
};

export default VerificationModel;
